import { APP_DOMAIN } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { SearchParams, UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { AppState } from '../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { Customer } from '../../models/customer';
import { PoolAutocompleteListAction } from '../pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { User } from '../../models/user';
import { resetCustomersSearch, updateCustomers } from '../../actions/customer-actions';

interface PartialCustomer extends BaseAutocompleteSuggestionType {
    name: string;
}

interface PoolCustomerAutocompleteListProps {
    items: Customer[];
    user: User;
    inProgress: boolean;
    updateCustomers: (searchParams: SearchParams) => void;
    resetCustomersSearch: () => void;
}

class PoolCustomerAutocompleteListBase extends PoolFormAutocompleteList<
    Customer,
    PartialCustomer,
    PoolCustomerAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];

    public override footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.contacts"
            url={urljoin(APP_DOMAIN, '/synergy/customers/new')}
        />
    );

    public override label = (<FormattedMessage id="app.label.customer" />);

    public override componentDidMount() {
        this.props.resetCustomersSearch();
        super.componentDidMount();
    }

    public getSuggestionValue = (suggestion: PartialCustomer) => suggestion && suggestion.name;

    public override handleValueChanged(search: string) {
        this.props.updateCustomers({ search });
    }

    public getPartial = (items: Customer[]): PartialCustomer[] => {
        return items.map(customer => {
            const editLink = urljoin(APP_DOMAIN, `/synergy/customers/${customer.id}/details`);
            return {
                id: padZeroLeft(customer.id, 5),
                name: customer.name,
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });
    };
}

const mapStateToProps = (state: AppState) => ({
    items: state.customers.sortedByName,
    inProgress: state.customers.requestingCustomer,
    user: state.user,
});

export const PoolCustomerAutocompleteList = connect(mapStateToProps, {
    updateCustomers,
    resetCustomersSearch,
})(PoolCustomerAutocompleteListBase);
